/**
 * @escapehub/token-creator - Token Data Reader
 *
 * Framework-agnostic pure functions for reading token data from the blockchain.
 * Supports both Multicall3 (batched) and individual RPC calls.
 *
 * @module read
 *
 * @example
 * ```typescript
 * import { readTokenData, createProvider, STANDARD_MULTICALL3_ADDRESS } from '@escapehub/token-creator';
 *
 * const provider = createProvider({ rpcUrl: 'https://rpc.sepolia.org', chainId: 11155111 });
 * const tokenData = await readTokenData(
 *   provider,
 *   '0x1234...5678',
 *   11155111,
 *   { multicall3Address: STANDARD_MULTICALL3_ADDRESS }
 * );
 * console.log(tokenData.name, tokenData.symbol);
 * ```
 */

import { ethers, JsonRpcProvider, Interface, Contract } from 'ethers';
import type {
  TokenDataFlat,
  DistributionEntryDetailed,
  ReadConfig,
  TokenIdentifier,
} from './types/token-data';

// ============================================================================
// Constants
// ============================================================================

/**
 * Standard Multicall3 address deployed on most EVM chains.
 * Verified at https://github.com/mds1/multicall
 *
 * @example
 * ```typescript
 * import { checkMulticall3Available, STANDARD_MULTICALL3_ADDRESS } from '@escapehub/token-creator';
 *
 * const available = await checkMulticall3Available(provider, STANDARD_MULTICALL3_ADDRESS);
 * ```
 */
export const STANDARD_MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11';

/**
 * Multicall3 ABI - only the aggregate3 function needed for batched reads.
 * This is the minimal ABI required for efficient multi-call operations.
 */
export const MULTICALL3_ABI = [
  {
    inputs: [
      {
        components: [
          { name: 'target', type: 'address' },
          { name: 'allowFailure', type: 'bool' },
          { name: 'callData', type: 'bytes' },
        ],
        name: 'calls',
        type: 'tuple[]',
      },
    ],
    name: 'aggregate3',
    outputs: [
      {
        components: [
          { name: 'success', type: 'bool' },
          { name: 'returnData', type: 'bytes' },
        ],
        name: 'returnData',
        type: 'tuple[]',
      },
    ],
    stateMutability: 'payable',
    type: 'function',
  },
] as const;

/**
 * Human-readable ABI strings for all token read functions.
 * Uses ethers v6 human-readable ABI format for clarity.
 */
export const TOKEN_READ_ABI = [
  // ERC20 Standard
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
  'function totalSupply() view returns (uint256)',

  // Ownership
  'function owner() view returns (address)',

  // Metadata
  'function metadataMutable() view returns (bool)',
  'function socialsMutable() view returns (bool)',
  'function website() view returns (string)',
  'function telegram() view returns (string)',
  'function xHandle() view returns (string)',
  'function discord() view returns (string)',

  // Core Features
  'function mintingEnabled() view returns (bool)',
  'function pausable() view returns (bool)',
  'function rescueEnabled() view returns (bool)',
  'function burnEnabled() view returns (bool)',
  'function hardCap() view returns (uint256)',

  // Module Flags
  'function feesModuleEnabled() view returns (bool)',
  'function limitsModuleEnabled() view returns (bool)',
  'function cooldownModuleEnabled() view returns (bool)',
  'function blacklistModuleEnabled() view returns (bool)',
  'function whitelistModuleEnabled() view returns (bool)',
  'function antiDumpModuleEnabled() view returns (bool)',
  'function deadblocksEnabled() view returns (bool)',

  // Fee Caps
  'function maxBuyFeeBps() view returns (uint16)',
  'function maxSellFeeBps() view returns (uint16)',
  'function maxTransferFeeBps() view returns (uint16)',
  'function maxCooldownSeconds() view returns (uint32)',

  // Current Fees
  'function feesEnabled() view returns (bool)',
  'function buyFeeBps() view returns (uint16)',
  'function sellFeeBps() view returns (uint16)',
  'function transferFeeBps() view returns (uint16)',
  'function feeRecipient() view returns (address)',
  'function feeBurnShareBps() view returns (uint16)',

  // Limits
  'function maxWalletAmount() view returns (uint256)',
  'function maxTxAmount() view returns (uint256)',
  'function limitsEndTime() view returns (uint48)',

  // Cooldown
  'function cooldownSeconds() view returns (uint32)',

  // Trading
  'function tradingEnabled() view returns (bool)',
  'function tradingEnabledAt() view returns (uint48)',
  'function router() view returns (address)',
  'function routerProtectionEnabled() view returns (bool)',

  // Blacklist
  'function blacklistFrozen() view returns (bool)',

  // Whitelist
  'function whitelistMode() view returns (uint8)',
  'function whitelistAutoDisable() view returns (bool)',
  'function whitelistMutable() view returns (bool)',
  'function whitelistActive() view returns (bool)',

  // Anti-Dump
  'function antiDumpType() view returns (uint8)',
  'function antiDumpLimit() view returns (uint256)',
  'function antiDumpPeriod() view returns (uint32)',
  'function antiDumpEndTime() view returns (uint48)',

  // Deadblock
  'function deadBlocks() view returns (uint8)',
  'function deadblockMode() view returns (uint8)',

  // Batch Transfer
  'function batchTransferAccess() view returns (uint8)',
  'function batchTransferFees() view returns (uint8)',
  'function maxBatchSize() view returns (uint16)',

  // State
  'function paused() view returns (bool)',
  'function permitEnabled() view returns (bool)',

  // Freeze States
  'function feesFrozen() view returns (bool)',
  'function limitsFrozen() view returns (bool)',
  'function cooldownFrozen() view returns (bool)',
  'function antiDumpFrozen() view returns (bool)',
  'function ownershipTransferBlocked() view returns (bool)',
] as const;

/**
 * Factory ABI for reading InitialDistribution events.
 */
export const FACTORY_EVENT_ABI = [
  'event InitialDistribution(address indexed token, address[] recipients, uint256[] amounts, string[] labels)',
] as const;

/**
 * Ordered list of token function names to call.
 * The order MUST match the destructuring in buildTokenData.
 */
export const TOKEN_FUNCTION_NAMES = [
  'name',
  'symbol',
  'decimals',
  'totalSupply',
  'owner',
  'metadataMutable',
  'socialsMutable',
  'website',
  'telegram',
  'xHandle',
  'discord',
  'mintingEnabled',
  'pausable',
  'rescueEnabled',
  'burnEnabled',
  'hardCap',
  'feesModuleEnabled',
  'limitsModuleEnabled',
  'cooldownModuleEnabled',
  'blacklistModuleEnabled',
  'maxBuyFeeBps',
  'maxSellFeeBps',
  'maxTransferFeeBps',
  'maxCooldownSeconds',
  'deadblocksEnabled',
  'deadBlocks',
  'deadblockMode',
  'whitelistModuleEnabled',
  'whitelistMode',
  'whitelistAutoDisable',
  'whitelistMutable',
  'whitelistActive',
  'antiDumpModuleEnabled',
  'antiDumpType',
  'antiDumpLimit',
  'antiDumpPeriod',
  'antiDumpEndTime',
  'batchTransferAccess',
  'batchTransferFees',
  'maxBatchSize',
  'feesEnabled',
  'buyFeeBps',
  'sellFeeBps',
  'transferFeeBps',
  'feeRecipient',
  'feeBurnShareBps',
  'tradingEnabled',
  'tradingEnabledAt',
  'router',
  'routerProtectionEnabled',
  'maxWalletAmount',
  'maxTxAmount',
  'limitsEndTime',
  'cooldownSeconds',
  'blacklistFrozen',
  'paused',
  'permitEnabled',
  'feesFrozen',
  'limitsFrozen',
  'cooldownFrozen',
  'antiDumpFrozen',
  'ownershipTransferBlocked',
] as const;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format a token amount to human-readable string.
 *
 * @param amount - Amount in smallest units (wei)
 * @param decimals - Token decimals
 * @returns Formatted string representation
 *
 * @example
 * ```typescript
 * formatAmount(1000000000000000000n, 18); // "1.0"
 * formatAmount(0n, 18); // "0"
 * ```
 */
export function formatAmount(amount: bigint, decimals: number): string {
  if (amount === 0n) return '0';
  return ethers.formatUnits(amount, decimals);
}

/**
 * Calculate percentage of total supply.
 *
 * @param amount - Amount in smallest units
 * @param totalSupply - Total supply in smallest units
 * @returns Percentage with 2 decimal precision (e.g., 5.25 for 5.25%)
 *
 * @example
 * ```typescript
 * calcPercent(1000n, 10000n); // 10.0
 * calcPercent(525n, 10000n);  // 5.25
 * ```
 */
export function calcPercent(amount: bigint, totalSupply: bigint): number {
  if (totalSupply === 0n || amount === 0n) return 0;
  return Number((amount * 10000n) / totalSupply) / 100;
}

/**
 * Build TokenData object from decoded multicall/individual call results.
 *
 * @param tokenAddress - Token contract address
 * @param decoded - Array of decoded values in order of TOKEN_FUNCTION_NAMES
 * @param decimals - Token decimals for formatting
 * @returns Complete TokenDataFlat object
 *
 * @example
 * ```typescript
 * const decoded = [...]; // From multicall or individual calls
 * const tokenData = buildTokenData('0x1234...', decoded, 18);
 * ```
 */
export function buildTokenData(
  tokenAddress: string,
  decoded: unknown[],
  decimals: number
): TokenDataFlat {
  // Destructure in same order as TOKEN_FUNCTION_NAMES
  const [
    name,
    symbol,
    _decimals,
    totalSupply,
    owner,
    metadataMutable,
    socialsMutable,
    website,
    telegram,
    xHandle,
    discord,
    mintingEnabled,
    pausable,
    rescueEnabled,
    burnEnabled,
    hardCap,
    feesModuleEnabled,
    limitsModuleEnabled,
    cooldownModuleEnabled,
    blacklistModuleEnabled,
    maxBuyFeeBps,
    maxSellFeeBps,
    maxTransferFeeBps,
    maxCooldownSeconds,
    deadblocksEnabled,
    deadBlocks,
    deadblockMode,
    whitelistModuleEnabled,
    whitelistMode,
    whitelistAutoDisable,
    whitelistMutable,
    whitelistActive,
    antiDumpModuleEnabled,
    antiDumpType,
    antiDumpLimit,
    antiDumpPeriod,
    antiDumpEndTime,
    batchTransferAccess,
    batchTransferFees,
    maxBatchSize,
    feesEnabled,
    buyFeeBps,
    sellFeeBps,
    transferFeeBps,
    feeRecipient,
    feeBurnShareBps,
    tradingEnabled,
    tradingEnabledAt,
    router,
    routerProtectionEnabled,
    maxWalletAmount,
    maxTxAmount,
    limitsEndTime,
    cooldownSeconds,
    blacklistFrozen,
    paused,
    permitEnabled,
    feesFrozen,
    limitsFrozen,
    cooldownFrozen,
    antiDumpFrozen,
    ownershipTransferBlocked,
  ] = decoded as [
    string, // name
    string, // symbol
    bigint | number, // decimals
    bigint, // totalSupply
    string, // owner
    boolean, // metadataMutable
    boolean, // socialsMutable
    string, // website
    string, // telegram
    string, // xHandle
    string, // discord
    boolean, // mintingEnabled
    boolean, // pausable
    boolean, // rescueEnabled
    boolean, // burnEnabled
    bigint, // hardCap
    boolean, // feesModuleEnabled
    boolean, // limitsModuleEnabled
    boolean, // cooldownModuleEnabled
    boolean, // blacklistModuleEnabled
    bigint | number, // maxBuyFeeBps
    bigint | number, // maxSellFeeBps
    bigint | number, // maxTransferFeeBps
    bigint | number, // maxCooldownSeconds
    boolean, // deadblocksEnabled
    bigint | number, // deadBlocks
    bigint | number, // deadblockMode
    boolean, // whitelistModuleEnabled
    bigint | number, // whitelistMode
    boolean, // whitelistAutoDisable
    boolean, // whitelistMutable
    boolean, // whitelistActive
    boolean, // antiDumpModuleEnabled
    bigint | number, // antiDumpType
    bigint, // antiDumpLimit
    bigint | number, // antiDumpPeriod
    bigint | number, // antiDumpEndTime
    bigint | number, // batchTransferAccess
    bigint | number, // batchTransferFees
    bigint | number, // maxBatchSize
    boolean, // feesEnabled
    bigint | number, // buyFeeBps
    bigint | number, // sellFeeBps
    bigint | number, // transferFeeBps
    string, // feeRecipient
    bigint | number, // feeBurnShareBps
    boolean, // tradingEnabled
    bigint | number, // tradingEnabledAt
    string, // router
    boolean, // routerProtectionEnabled
    bigint, // maxWalletAmount
    bigint, // maxTxAmount
    bigint | number, // limitsEndTime
    bigint | number, // cooldownSeconds
    boolean, // blacklistFrozen
    boolean, // paused
    boolean, // permitEnabled
    boolean, // feesFrozen
    boolean, // limitsFrozen
    boolean, // cooldownFrozen
    boolean, // antiDumpFrozen
    boolean, // ownershipTransferBlocked
  ];

  const supply = totalSupply as bigint;

  return {
    address: tokenAddress,
    name: name as string,
    symbol: symbol as string,
    decimals,
    totalSupply: supply,
    totalSupplyFormatted: formatAmount(supply, decimals),
    owner: owner as string,
    metadataMutable: metadataMutable as boolean,
    socialsMutable: socialsMutable as boolean,
    website: website as string,
    telegram: telegram as string,
    xHandle: xHandle as string,
    discord: discord as string,
    mintingEnabled: mintingEnabled as boolean,
    pausable: pausable as boolean,
    rescueEnabled: rescueEnabled as boolean,
    burnEnabled: burnEnabled as boolean,
    hardCap: hardCap as bigint,
    hardCapFormatted: formatAmount(hardCap as bigint, decimals),
    feesModuleEnabled: feesModuleEnabled as boolean,
    limitsModuleEnabled: limitsModuleEnabled as boolean,
    cooldownModuleEnabled: cooldownModuleEnabled as boolean,
    blacklistModuleEnabled: blacklistModuleEnabled as boolean,
    whitelistModuleEnabled: whitelistModuleEnabled as boolean,
    antiDumpModuleEnabled: antiDumpModuleEnabled as boolean,
    deadblocksEnabled: deadblocksEnabled as boolean,
    maxBuyFeeBps: Number(maxBuyFeeBps),
    maxSellFeeBps: Number(maxSellFeeBps),
    maxTransferFeeBps: Number(maxTransferFeeBps),
    maxCooldownSeconds: Number(maxCooldownSeconds),
    feesEnabled: feesEnabled as boolean,
    buyFeeBps: Number(buyFeeBps),
    sellFeeBps: Number(sellFeeBps),
    transferFeeBps: Number(transferFeeBps),
    feeRecipient: feeRecipient as string,
    feeBurnShareBps: Number(feeBurnShareBps),
    maxWalletAmount: maxWalletAmount as bigint,
    maxWalletFormatted: formatAmount(maxWalletAmount as bigint, decimals),
    maxWalletPercent: calcPercent(maxWalletAmount as bigint, supply),
    maxTxAmount: maxTxAmount as bigint,
    maxTxFormatted: formatAmount(maxTxAmount as bigint, decimals),
    maxTxPercent: calcPercent(maxTxAmount as bigint, supply),
    limitsEndTime: Number(limitsEndTime),
    cooldownSeconds: Number(cooldownSeconds),
    tradingEnabled: tradingEnabled as boolean,
    tradingEnabledAt: Number(tradingEnabledAt),
    router: router as string,
    routerProtectionEnabled: routerProtectionEnabled as boolean,
    blacklistFrozen: blacklistFrozen as boolean,
    whitelistMode: Number(whitelistMode),
    whitelistAutoDisable: whitelistAutoDisable as boolean,
    whitelistMutable: whitelistMutable as boolean,
    whitelistActive: whitelistActive as boolean,
    antiDumpType: Number(antiDumpType),
    antiDumpLimit: antiDumpLimit as bigint,
    antiDumpLimitFormatted: formatAmount(antiDumpLimit as bigint, decimals),
    antiDumpPeriod: Number(antiDumpPeriod),
    antiDumpEndTime: Number(antiDumpEndTime),
    deadBlocks: Number(deadBlocks),
    deadblockMode: Number(deadblockMode),
    batchTransferAccess: Number(batchTransferAccess),
    batchTransferFees: Number(batchTransferFees),
    maxBatchSize: Number(maxBatchSize),
    paused: paused as boolean,
    whitelistFrozen: !(whitelistMutable as boolean), // whitelistMutable=false means frozen
    permitEnabled: permitEnabled as boolean,
    feesFrozen: feesFrozen as boolean,
    limitsFrozen: limitsFrozen as boolean,
    cooldownFrozen: cooldownFrozen as boolean,
    antiDumpFrozen: antiDumpFrozen as boolean,
    ownershipTransferBlocked: ownershipTransferBlocked as boolean,
  };
}

// ============================================================================
// Provider Utilities
// ============================================================================

/**
 * Create a JsonRpcProvider from configuration.
 *
 * @param config - Configuration with rpcUrl and optional chainId
 * @returns Configured JsonRpcProvider instance
 *
 * @example
 * ```typescript
 * const provider = createProvider({
 *   rpcUrl: 'https://rpc.sepolia.org',
 *   chainId: 11155111
 * });
 * ```
 */
export function createProvider(config: {
  rpcUrl: string;
  chainId?: number;
}): JsonRpcProvider {
  return new JsonRpcProvider(config.rpcUrl, config.chainId);
}

/**
 * Check if Multicall3 is available at the given address.
 *
 * @param provider - JsonRpcProvider instance
 * @param multicallAddress - Address to check (defaults to STANDARD_MULTICALL3_ADDRESS)
 * @returns True if Multicall3 contract exists at address
 *
 * @example
 * ```typescript
 * const available = await checkMulticall3Available(provider);
 * if (available) {
 *   // Use batched reads
 * } else {
 *   // Fall back to individual calls
 * }
 * ```
 */
export async function checkMulticall3Available(
  provider: JsonRpcProvider,
  multicallAddress: string = STANDARD_MULTICALL3_ADDRESS
): Promise<boolean> {
  try {
    const code = await provider.getCode(multicallAddress);
    return code !== '0x' && code.length > 2;
  } catch {
    return false;
  }
}

// ============================================================================
// Core Read Functions
// ============================================================================

/**
 * Read token data using Multicall3 - batches all calls into a single RPC request.
 * This is approximately 60x more efficient than individual calls.
 *
 * @param provider - JsonRpcProvider instance
 * @param tokenAddress - Token contract address
 * @param multicallAddress - Multicall3 contract address
 * @returns Complete TokenDataFlat object
 * @throws Error if any multicall fails
 *
 * @example
 * ```typescript
 * const provider = createProvider({ rpcUrl: 'https://rpc.sepolia.org' });
 * const tokenData = await readTokenDataViaMulticall(
 *   provider,
 *   '0x1234...5678',
 *   STANDARD_MULTICALL3_ADDRESS
 * );
 * ```
 */
export async function readTokenDataViaMulticall(
  provider: JsonRpcProvider,
  tokenAddress: string,
  multicallAddress: string
): Promise<TokenDataFlat> {
  const multicall = new Contract(multicallAddress, MULTICALL3_ABI, provider);
  const tokenInterface = new Interface(TOKEN_READ_ABI);

  // Build Multicall3 calls array
  const calls = TOKEN_FUNCTION_NAMES.map((fn) => ({
    target: tokenAddress,
    allowFailure: false,
    callData: tokenInterface.encodeFunctionData(fn),
  }));

  // Execute single batched call
  const results: { success: boolean; returnData: string }[] =
    await multicall.aggregate3.staticCall(calls);

  // Decode all results
  const decoded = results.map((result, i) => {
    if (!result.success) {
      throw new Error(`Multicall failed for ${TOKEN_FUNCTION_NAMES[i]}`);
    }
    return tokenInterface.decodeFunctionResult(
      TOKEN_FUNCTION_NAMES[i],
      result.returnData
    )[0];
  });

  // Get decimals from decoded results (index 2)
  const decimals = Number(decoded[2]);

  return buildTokenData(tokenAddress, decoded, decimals);
}

/**
 * Read token data using individual calls - fallback for chains without Multicall3.
 * Makes approximately 60 parallel RPC requests.
 *
 * @param provider - JsonRpcProvider instance
 * @param tokenAddress - Token contract address
 * @returns Complete TokenDataFlat object
 *
 * @example
 * ```typescript
 * const provider = createProvider({ rpcUrl: 'https://rpc.pepeunchained.com' });
 * const tokenData = await readTokenDataIndividual(provider, '0x1234...5678');
 * ```
 */
export async function readTokenDataIndividual(
  provider: JsonRpcProvider,
  tokenAddress: string
): Promise<TokenDataFlat> {
  const contract = new Contract(tokenAddress, TOKEN_READ_ABI, provider);

  // Read all data in parallel for performance
  const decoded = await Promise.all(
    TOKEN_FUNCTION_NAMES.map((fn) =>
      (contract[fn] as () => Promise<unknown>)()
    )
  );

  // Get decimals from decoded results (index 2)
  const decimals = Number(decoded[2]);

  return buildTokenData(tokenAddress, decoded, decimals);
}

/**
 * Read all token data from the blockchain.
 * Automatically uses Multicall3 if available, otherwise falls back to individual calls.
 *
 * @param provider - JsonRpcProvider instance
 * @param tokenAddress - Token contract address
 * @param chainId - Chain ID (used for logging/debugging)
 * @param config - Optional configuration with multicall3Address
 * @returns Complete TokenDataFlat object
 *
 * @example
 * ```typescript
 * // With Multicall3 (recommended)
 * const provider = createProvider({ rpcUrl: 'https://rpc.sepolia.org', chainId: 11155111 });
 * const tokenData = await readTokenData(
 *   provider,
 *   '0x1234...5678',
 *   11155111,
 *   { multicall3Address: STANDARD_MULTICALL3_ADDRESS }
 * );
 *
 * // Without Multicall3 (falls back to individual calls)
 * const tokenData = await readTokenData(
 *   provider,
 *   '0x1234...5678',
 *   97741 // Pepe Unchained - no Multicall3
 * );
 * ```
 */
export async function readTokenData(
  provider: JsonRpcProvider,
  tokenAddress: string,
  _chainId: number,
  config?: ReadConfig
): Promise<TokenDataFlat> {
  const multicallAddress = config?.multicall3Address;

  // Use Multicall3 if available (batches ~60 calls into 1 RPC request)
  if (multicallAddress) {
    return readTokenDataViaMulticall(provider, tokenAddress, multicallAddress);
  }

  // Fall back to individual calls for chains without Multicall3
  return readTokenDataIndividual(provider, tokenAddress);
}

/**
 * Read multiple tokens in parallel.
 * Useful for fetching data for a list of tokens (e.g., launchpad).
 *
 * @param tokens - Array of token identifiers (address + chainId)
 * @param configs - Map of chainId to ReadConfig (with multicall3Address)
 * @param providerFactory - Function to create/retrieve provider for a chain
 * @returns Array of TokenDataFlat (in same order as input tokens)
 *
 * @example
 * ```typescript
 * const tokens = [
 *   { address: '0x1234...', chainId: 11155111 },
 *   { address: '0x5678...', chainId: 11155111 },
 *   { address: '0x9abc...', chainId: 1 },
 * ];
 *
 * const configs = {
 *   11155111: { multicall3Address: STANDARD_MULTICALL3_ADDRESS },
 *   1: { multicall3Address: STANDARD_MULTICALL3_ADDRESS },
 * };
 *
 * const providerCache = new Map<number, JsonRpcProvider>();
 * const getProvider = (chainId: number) => {
 *   if (!providerCache.has(chainId)) {
 *     providerCache.set(chainId, createProvider({ rpcUrl: RPC_URLS[chainId], chainId }));
 *   }
 *   return providerCache.get(chainId)!;
 * };
 *
 * const results = await readMultipleTokens(tokens, configs, getProvider);
 * ```
 */
export async function readMultipleTokens(
  tokens: TokenIdentifier[],
  configs: Record<number, ReadConfig>,
  providerFactory: (chainId: number) => JsonRpcProvider
): Promise<TokenDataFlat[]> {
  const promises = tokens.map((token) => {
    const provider = providerFactory(token.chainId);
    const config = configs[token.chainId];
    return readTokenData(provider, token.address, token.chainId, config);
  });

  return Promise.all(promises);
}

// ============================================================================
// Event Reading Functions
// ============================================================================

/**
 * Fetch initial distribution data from the factory's InitialDistribution event.
 * Also calculates and includes the owner's remainder if any tokens weren't explicitly distributed.
 *
 * @param provider - JsonRpcProvider instance
 * @param tokenAddress - Token contract address
 * @param factoryAddress - LaunchERC20Factory contract address
 * @param decimals - Token decimals for formatting
 * @param totalSupply - Total supply for percentage calculations
 * @param ownerAddress - Optional owner address to calculate remainder
 * @param lookbackBlocks - Number of blocks to look back (default: 10000)
 * @returns Array of DistributionEntryDetailed objects
 *
 * @example
 * ```typescript
 * const distribution = await fetchInitialDistribution(
 *   provider,
 *   '0x1234...5678',
 *   '0xfactory...',
 *   18,
 *   1000000000000000000000000n,
 *   '0xowner...'
 * );
 *
 * for (const entry of distribution) {
 *   console.log(`${entry.label}: ${entry.percent}% (${entry.amountFormatted})`);
 * }
 * ```
 */
export async function fetchInitialDistribution(
  provider: JsonRpcProvider,
  tokenAddress: string,
  factoryAddress: string,
  decimals: number,
  totalSupply: bigint,
  ownerAddress?: string,
  lookbackBlocks: number = 10000
): Promise<DistributionEntryDetailed[]> {
  const factory = new Contract(factoryAddress, FACTORY_EVENT_ABI, provider);

  try {
    // Query logs for InitialDistribution event for this specific token
    const filter = factory.filters.InitialDistribution(tokenAddress);
    const logs = await factory.queryFilter(filter, -lookbackBlocks);

    if (logs.length === 0) {
      return [];
    }

    // Take the most recent event (should only be one per token)
    const event = logs[logs.length - 1];
    const parsed = factory.interface.parseLog({
      topics: event.topics as string[],
      data: event.data,
    });

    if (!parsed) {
      return [];
    }

    const recipients = parsed.args.recipients as string[];
    const amounts = parsed.args.amounts as bigint[];
    const labels = parsed.args.labels as string[];

    // Format the distribution entries
    const entries: DistributionEntryDetailed[] = [];
    let totalDistributed = 0n;

    for (let i = 0; i < recipients.length; i++) {
      const amount = amounts[i];
      totalDistributed += amount;

      const percent = calcPercent(amount, totalSupply);

      const label = labels[i] || '';
      // Mark entries with "Owner" label as owner remainder (new tokens include owner in event)
      const isOwnerEntry =
        label === 'Owner' &&
        !!ownerAddress &&
        recipients[i].toLowerCase() === ownerAddress.toLowerCase();

      entries.push({
        recipient: recipients[i],
        amount,
        amountFormatted: formatAmount(amount, decimals),
        percent,
        label,
        isOwnerRemainder: isOwnerEntry,
      });
    }

    // Calculate and add owner's remainder if there's any (backward compatibility for old tokens)
    // New tokens will have owner included in the event, so totalDistributed will equal totalSupply
    if (ownerAddress && totalSupply > totalDistributed) {
      const remainderAmount = totalSupply - totalDistributed;
      const remainderPercent = calcPercent(remainderAmount, totalSupply);

      entries.push({
        recipient: ownerAddress,
        amount: remainderAmount,
        amountFormatted: formatAmount(remainderAmount, decimals),
        percent: remainderPercent,
        label: 'Owner',
        isOwnerRemainder: true,
      });
    }

    return entries;
  } catch (err) {
    console.warn('Failed to fetch initial distribution:', err);
    return [];
  }
}

// ============================================================================
// Re-exports
// ============================================================================

export type {
  TokenDataFlat,
  DistributionEntryDetailed,
  ReadConfig,
  TokenIdentifier,
};
